/* iceberg-ui */
import { PieChart } from '@auviknetworks/iceberg-ui';

/* MUI */
import { ArrowDownward, ArrowUpward, Clear } from '@mui/icons-material';
import { Box, Chip, Grid, TableCell, Typography } from '@mui/material';
import { withTheme } from '@mui/styles';

/*  Shared  */
import { ApplicationNameAdminAppModalCell } from 'application/Shared/Components/ApplicationNameAdminAppModalCell';
import { Sources, UserBubbles } from 'application/Shared/Components/Avatars';
import {
    DateFormat,
    getDateTime
} from 'application/Shared/Components/Core/DateFormat';
import {
    LifecyclePicker,
    RiskPicker
} from 'application/Shared/Components/Pickers';
import { popoverContentsColumns } from 'application/Shared/Components/Recharts/PopoverContents';
import { ManagedTable } from 'application/Shared/Components/Table';
import { TagLinksList } from 'application/Shared/Components/Tag';
import { HMS } from 'application/Shared/Components/Usage';
import { FontSizes } from 'application/Shared/Constants';
import {
    Helpers,
    hmsFormatter,
    hmsUsageFormatter
} from 'application/Shared/Helpers';
import Core from 'application/Shared/Helpers/Functions';

/*  Yarn  */
import i18n from 'i18n';

/*  Objects  */
import { Application, ApplicationBusinessOwner } from 'objects/Application';
import { User, UserIconAndName } from 'objects/User';

/* React */
import React, { Component } from 'react';
import { Trans, withTranslation } from 'react-i18next';
import { Sparklines, SparklinesBars, SparklinesLine } from 'react-sparklines';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    XAxis,
    YAxis
} from 'recharts';

/*  Components  */
import CoverPage from '../Components/Containers/CoverPage';

/*  Constants  */
let low = 180;
let medium = 30 * 60;
let high = 120 * 60;

const monthUsageGroupNames = {
    high: 'High Usage',
    low: 'Low Usage',
    medium: 'Medium Usage',
    minimum: 'Minimum Usage'
};

const ProductAdoptionReports = (props) => {
    const { reportData, reportSetup, renderingScheduledReport, runReport } =
        props;

    if (typeof reportData === 'undefined') {
        return null;
    }

    return (
        <Box data-trackid="adoptionReportsMainRender" width="100%">
            {reportSetup.reportSelections
                ?.productAdoptionProductAdoptionCover && (
                <CoverPage {...props} />
            )}
            {reportData.productAdoptionProductAdoptionOverview && (
                <Box m="32px">
                    <ProductAdoptionBreakdown
                        {...props}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={reportData}
                        runReport={runReport}
                    />
                </Box>
            )}
        </Box>
    );
};

const monthUsageGroupCalculator = (value) => {
    if (value < low) {
        return monthUsageGroupNames.minimum;
    }
    if (value < medium) {
        return monthUsageGroupNames.low;
    }
    if (value < high) {
        return monthUsageGroupNames.medium;
    }

    return monthUsageGroupNames.high;
};

const monthUsageFormat = (value) => {
    if (value < low) {
        return {
            cellIcon: (
                <Clear
                    sx={{ color: 'usage.icon.waste', fontSize: FontSizes[3] }}
                />
            ),
            cellStyle: { color: 'usage.textColor.waste', textAlign: 'center' }
        };
    }
    if (value < medium) {
        return {
            cellIcon: (
                <ArrowDownward
                    sx={{ color: 'usage.icon.low', fontSize: FontSizes[3] }}
                />
            ),
            cellStyle: { color: 'usage.textColor.low', textAlign: 'center' }
        };
    }
    if (value < high) {
        return {
            cellIcon: (
                <ArrowUpward
                    sx={{ color: 'usage.icon.medium', fontSize: FontSizes[3] }}
                />
            ),
            cellStyle: { color: 'usage.textColor.medium', textAlign: 'center' }
        };
    }

    return {
        cellIcon: (
            <>
                <ArrowUpward
                    sx={{
                        color: 'usage.icon.high',
                        fontSize: FontSizes[3],
                        marginRight: '-5px'
                    }}
                />
                <ArrowUpward
                    sx={{ color: 'usage.icon.high', fontSize: FontSizes[3] }}
                />
            </>
        ),
        cellStyle: { color: 'usage.textColor.high', textAlign: 'center' }
    };
};

const renderSources = (row) => {
    if (!row.link) {
        return null;
    }

    // Link will return true/false if a source was found, but not numbers, like in other instances.
    const sources = Helpers.getSourcesViaLink(row.link.sources);

    return <Sources sources={sources} max={5} noBadge />;
};

const getDate = (date) => getDateTime({ date, noTimeString: true });

const getHMSText = (seconds) => (
    <Trans
        i18nKey="Reports.adoption.hms.sec.usageOverNumberOvertotalDays"
        components={{
            HMS: (
                <HMS
                    seconds={seconds}
                    noSeconds
                    includeText
                    sx={{
                        fontSize: FontSizes[0]
                    }}
                />
            )
        }}
    />
);

const headersMultipleApps = [
    {
        align: 'left',
        field: 'name',
        renderHeader: i18n.t('Common.application'),
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'application.name',
        tooltip: 'application',
        width: '30%'
    },
    {
        align: 'left',
        field: 'status',
        renderHeader: i18n.t('Common.lifecycleStage'),
        tooltip: 'lifecycle_stage',
        width: '20%'
    },
    {
        align: 'left',
        field: 'business_owner',
        renderHeader: i18n.t('Common.business_owner'),
        tooltip: 'business_owner',
        width: '25%'
    }
];

const headersTop5Users = ({ t, totalDays }) => [
    {
        align: 'left',
        field: 'rank',
        renderHeader: t('Common.user'),
        width: '30%'
    },
    {
        align: 'left',
        field: 'name',
        renderHeader: (
            <Typography style={{ fontWeight: 600 }}>
                {t('Common.total_usage')}
                <br />
                <small>
                    (
                    {t('Reports.adoption.dailyAveargeOverDays', {
                        totalDays
                    })}
                    )
                </small>
            </Typography>
        ),
        width: '30%'
    }
];

const headersBottom5Users = ({ t, totalDays }) => [
    {
        align: 'left',
        field: 'rank',
        renderHeader: t('Common.user'),
        width: '30%'
    },
    {
        align: 'left',
        field: 'name',
        renderHeader: (
            <Typography style={{ fontWeight: 600 }}>
                {t('Common.total_usage')}
                <br />
                <small>
                    {t('Reports.adoption.dailyAveargeOverDays', {
                        totalDays
                    })}
                </small>
            </Typography>
        ),
        width: '30%'
    }
];

const headersDailyBreakdown = [
    {
        align: 'left',
        field: 'f_rank',
        renderHeader: i18n.t('Common.date'),
        width: '25%'
    },
    {
        align: 'left',
        field: 'f_name',
        renderHeader: i18n.t('Common.total_usage'),
        width: '25%'
    },
    {
        align: 'left',
        field: 'f_bb',
        renderHeader: i18n.t('Common.user_count'),
        width: '25%'
    },
    {
        align: 'left',
        field: 'f_u',
        renderHeader: i18n.t('Common.users'),
        width: '25%'
    }
];

const headersUserBreakdownSingle = ({ t, totalDays }) => [
    {
        align: 'left',
        field: 'a_rank',
        renderHeader: t('Common.rank'),
        width: '5%'
    },
    {
        align: 'left',
        field: 'a_name',
        renderHeader: t('Common.user'),
        width: '30%'
    },
    {
        align: 'left',
        colSpan: 2,
        field: 'a_aa',
        renderHeader: (
            <Typography style={{ fontWeight: 600 }}>
                {t('Common.total_usage')}
                <br />
                <small>
                    {t('Reports.adoption.dailyAveargeOverDays', {
                        totalDays
                    })}
                </small>
            </Typography>
        ),
        width: '30%'
    },
    {
        align: 'left',
        field: 'a_source',
        renderHeader: t('Common.source'),
        width: '10%'
    },
    {
        align: 'left',
        field: 'a_expertise',
        renderHeader: t('Common.total_usage'),
        width: '10%'
    }
];

const headersUserBreakdownMultiple = ({ t, totalDays }) => [
    {
        align: 'left',
        field: 'a_rank',
        renderHeader: t('Common.rank'),
        width: '5%'
    },
    {
        align: 'left',
        field: 'a_name',
        renderHeader: t('Common.user'),
        width: '30%'
    },
    {
        align: 'left',
        colSpan: 2,
        field: 'a_aa',
        renderHeader: (
            <Typography style={{ fontWeight: 600 }}>
                {t('Common.total_usage')}
                <br />
                <small>
                    {t('Reports.adoption.dailyAveargeOverDays', {
                        totalDays
                    })}
                </small>
            </Typography>
        ),
        width: '30%'
    }
];

const searchFields = [
    'application.name',
    'application.category',
    'application.subCategory'
];

class ProductAdoptionBreakdown extends Component {
    constructor(props) {
        super(props);

        this.state = {
            breakdownChart: [],
            loaded: false,
            userChart: []
        };
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        const { reportData, t } = this.props;
        const { productAdoptionProductAdoptionOverview } = reportData;
        const { userUsageData, dailyUsageData } =
            productAdoptionProductAdoptionOverview;

        const userChart = [];
        const breakdownChart = [];
        const usageBreakdown = { Active: 0, Inactive: 0 };

        const userCount = Object.keys(userUsageData).length;
        const dayCount = Object.keys(dailyUsageData).length;

        low *= dayCount / 30;
        medium *= dayCount / 30;
        high *= dayCount / 30;

        let maxUsageChart = 0;

        usageBreakdown.Active = parseInt(userCount, 10);
        usageBreakdown.Inactive =
            parseInt(reportData.userCount, 10) - userCount;

        const categorizedUsageBreakdown = {};

        Object.entries(userUsageData).forEach(([i, userUsageDatum]) => {
            if (!userUsageDatum) {
                return;
            }
            let links;
            let user;

            if (reportData.links) {
                links = reportData.links[i];
            }

            if (reportData.users) {
                user = reportData.users[i];
            }

            const usage = userUsageDatum;

            userChart.push({
                link: links,
                usage,
                user
            });
            const usageType = monthUsageGroupCalculator(usage / 1000);
            if (!Core.isset(categorizedUsageBreakdown, usageType)) {
                categorizedUsageBreakdown[usageType] = 0;
            }
            categorizedUsageBreakdown[usageType] += 1;

            // TODO: Fix eslint issues. I could not find an example with links
            if (reportData.links && reportData.links[i]) {
                // eslint-disable-next-line no-restricted-syntax
                for (const j in reportData.links[i].usageChart) {
                    if (reportData.links[i].usageChart[j]) {
                        const value = reportData.links[i].usageChart[j];
                        if (value > maxUsageChart) maxUsageChart = value;
                    }
                }
            }
        });

        userChart.sort((a, b) => b.usage - a.usage);

        const augmentedUserChart = userChart.map((chart, index) => ({
            ...chart,
            rank: parseInt(index, 10) + 1
        }));

        const categorizedUsageBreakdownArray = [];

        Object.values(monthUsageGroupNames).forEach((groupName) => {
            categorizedUsageBreakdownArray.push({
                type: groupName,
                value: Core.isset(categorizedUsageBreakdown, groupName)
                    ? categorizedUsageBreakdown[groupName]
                    : 0
            });
        });

        Object.entries(dailyUsageData).forEach(([i, rows]) => {
            if (!rows) {
                return;
            }

            let usage = 0;
            Object.values(rows).forEach((value) => {
                usage += value;
            });

            const users = Object.keys(rows).length;
            const userListArray = Object.keys(rows);
            const userList = [];
            userListArray.forEach((userKey) => {
                if (reportData.users && reportData.users[userKey]) {
                    userList.push(reportData.users[userKey]);
                }
            });

            const date = new Date(parseInt(i, 10));
            breakdownChart.push({
                date: parseInt(i, 10),
                name: date.toLocaleString(undefined, {
                    day: '2-digit',
                    month: '2-digit'
                }),
                usage,
                userList,
                users
            });
        });

        const usageBreakdownArray = [];
        usageBreakdownArray.push({
            name: t('Common.active'),
            value: usageBreakdown.Active
        });
        usageBreakdownArray.push({
            name: t('Common.inactive'),
            value: usageBreakdown.Inactive
        });

        const totalDays = breakdownChart.length;
        breakdownChart.sort((a, b) => a.date - b.date);
        this.setState({
            breakdownChart,
            categorizedUsageBreakdownArray,
            loaded: true,
            maxUsageChart,
            totalDays,
            usageBreakdown,
            usageBreakdownArray,
            userChart: augmentedUserChart
        });
    };

    renderRowMultipleApps = (row) => {
        const { runReport } = this.props;

        return (
            <Application data={row.application}>
                <TableCell>
                    <ApplicationNameAdminAppModalCell
                        onCloseModal={runReport}
                        row={row}
                        withCategory
                    />
                </TableCell>
                <TableCell align="left">
                    <LifecyclePicker visualOnly value={row.status} />
                </TableCell>
                <TableCell>
                    <ApplicationBusinessOwner
                        businessOwnerPlatformUserId={
                            row.businessOwnerPlatformUserId
                        }
                        noBusinessOwnerRequired={row.noBusinessOwnerRequired}
                        managedByPartner={row.managedByPartner}
                        managed3rdParty={row.managed3rdParty}
                    />
                </TableCell>
            </Application>
        );
    };

    renderRowTop5Users = (row) => {
        const { totalDays } = this.state;

        return (
            <>
                <TableCell>
                    <User data={row.user}>
                        <UserIconAndName link />
                    </User>
                </TableCell>
                <TableCell>
                    <HMS seconds={row.usage / 1000} noSeconds includeText />
                    <br />
                    <small>{getHMSText(row.usage / 1000 / totalDays)}</small>
                </TableCell>
            </>
        );
    };

    renderRowBottom5Users = (row) => {
        const { totalDays } = this.state;

        return (
            <>
                <TableCell>
                    <User data={row.user}>
                        <UserIconAndName link />
                    </User>
                </TableCell>
                <TableCell>
                    <HMS seconds={row.usage / 1000} noSeconds includeText />
                    <br />
                    <small>{getHMSText(row.usage / 1000 / totalDays)}</small>
                </TableCell>
            </>
        );
    };

    renderRowUserBreakdown = (row) => {
        const { reportData, t, theme } = this.props;
        const { maxUsageChart, totalDays } = this.state;

        const multipleApps = reportData?.application?.length > 1;

        return (
            <>
                <TableCell align="center">
                    <Chip label={row.rank} size="small" variant="rank" />
                </TableCell>
                <TableCell>
                    <User data={row.user}>
                        <UserIconAndName link />
                    </User>
                </TableCell>
                <TableCell sx={monthUsageFormat(row.usage / 1000).cellStyle}>
                    {hmsUsageFormatter({
                        defaultMessage: t('Common.minimumUsage'),
                        t,
                        value: row.usage / 1000
                    })}
                    <br />
                    <small>{getHMSText(row.usage / 1000 / totalDays)}</small>
                </TableCell>

                <TableCell sx={monthUsageFormat(row.usage / 1000).cellStyle}>
                    {monthUsageFormat(row.usage / 1000).cellIcon}
                    &nbsp;
                    <Typography style={{ fontWeight: 600 }}>
                        {monthUsageGroupCalculator(row.usage / 1000)}
                    </Typography>
                </TableCell>

                {!multipleApps && (
                    <>
                        <TableCell>{renderSources(row)}</TableCell>
                        <TableCell>
                            {row.link && row.link.usageChart && (
                                <div
                                    style={{
                                        width: '75px'
                                    }}
                                >
                                    <Sparklines
                                        data={Core.padArray(
                                            row.link.usageChart,
                                            20,
                                            0
                                        )}
                                        max={maxUsageChart}
                                    >
                                        <SparklinesBars
                                            style={{
                                                fill: theme.palette.sparkLines
                                                    .sparkBar,
                                                stroke: theme.palette.sparkLines
                                                    .stroke
                                            }}
                                        />
                                        <SparklinesLine
                                            color={
                                                theme.palette.sparkLines
                                                    .sparkLine
                                            }
                                            style={{
                                                fill: 'none'
                                            }}
                                        />
                                    </Sparklines>
                                </div>
                            )}
                        </TableCell>
                    </>
                )}
            </>
        );
    };

    // eslint-disable-next-line class-methods-use-this
    renderRowDailyBreakdown = (row) => (
        <>
            <TableCell>
                <DateFormat date={row.date} noTimeString />
            </TableCell>
            <TableCell>
                <HMS seconds={row.usage / 1000} />
            </TableCell>
            <TableCell>{row.users}</TableCell>
            <TableCell>
                <UserBubbles
                    dataIsUser
                    max={10}
                    users={row.userList}
                    align="right"
                />
            </TableCell>
        </>
    );

    render() {
        const {
            breakdownChart,
            categorizedUsageBreakdownArray,
            loaded,
            totalDays,
            usageBreakdownArray,
            usageBreakdown,
            userChart
        } = this.state;
        const { reportData, reportSetup, runReport, t, theme } = this.props;

        if (!loaded) {
            return null;
        }

        if (!Core.isset(this.props, 'reportData', 'application')) {
            return (
                <Typography variant="p" color="text.error">
                    {t('Reports.adoption.selectApps')}
                </Typography>
            );
        }

        const multipleApps = reportData?.application?.length > 1;

        const rowsMultipleApps = reportData?.application;

        return (
            <>
                <Box width="100%">
                    {multipleApps ? (
                        <Box mb="32px">
                            <Typography
                                variant="h4"
                                style={{ marginBottom: '16px' }}
                            >
                                {t('Reports.adoption.multipleApps')}
                            </Typography>
                            <ManagedTable
                                headers={headersMultipleApps}
                                renderBody={this.renderRowMultipleApps}
                                renderBodyKey="id"
                                renderFooter={false}
                                rows={rowsMultipleApps}
                                rowsPerPage={-1}
                                searchEnabled={false}
                                searchFields={searchFields}
                                sortDirection="DESC"
                                sortFieldType="string"
                                sortKey="application.name"
                            />
                        </Box>
                    ) : (
                        <Box width="100%" mb="32px">
                            <Typography variant="h4">
                                <Trans
                                    i18nKey="Reports.adoption.forApp.forPeriod"
                                    values={{
                                        appName:
                                            reportData.application[0]
                                                .application.name
                                    }}
                                    components={{
                                        EndDate: (
                                            <>{getDate(reportSetup.endTime)}</>
                                        ),
                                        StartDate: (
                                            <>
                                                {getDate(reportSetup.startTime)}
                                            </>
                                        )
                                    }}
                                />
                            </Typography>
                            <hr />
                            <Application
                                data={reportData.application[0].application}
                            >
                                <Typography
                                    component="span"
                                    style={{
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    <ApplicationNameAdminAppModalCell
                                        onCloseModal={runReport}
                                        row={reportData.application[0]}
                                        withCategory
                                        categoryNoTruncate
                                    />
                                </Typography>
                                <Box mt="8px">
                                    <Grid container>
                                        <Grid item xs={3}>
                                            <Typography
                                                style={{ fontWeight: 600 }}
                                            >
                                                {t('Common.risk')}:
                                            </Typography>
                                            <Box>
                                                {reportData.application[0]
                                                    .application.risk ? (
                                                    <RiskPicker
                                                        visualOnly
                                                        value={
                                                            reportData
                                                                .application[0]
                                                                .application
                                                                .risk
                                                        }
                                                    />
                                                ) : (
                                                    <RiskPicker
                                                        unselectedOption={t(
                                                            'Common.no_risk_assigned'
                                                        )}
                                                        visualOnly
                                                        unselectedValue={null}
                                                        value={null}
                                                    />
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={5}>
                                            <Typography
                                                style={{ fontWeight: 600 }}
                                            >
                                                {t('Common.tags')}:
                                            </Typography>
                                            <Box>
                                                <TagLinksList
                                                    tagLinks={
                                                        reportData
                                                            .application[0]
                                                            ?.tagLinks
                                                    }
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography
                                                style={{ fontWeight: 600 }}
                                            >
                                                {t('Common.lifecycleStage')}:
                                            </Typography>
                                            <Box>
                                                <LifecyclePicker
                                                    visualOnly
                                                    value={
                                                        reportData
                                                            .application[0]
                                                            .status
                                                    }
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Box mt="16px">
                                    <Typography style={{ fontWeight: 600 }}>
                                        {t('Common.business_purpose')}:
                                    </Typography>
                                    <Box>
                                        {reportData.application[0].purpose
                                            ? reportData.application[0].purpose
                                            : t('Common.no_purpose_set')}
                                    </Box>
                                </Box>
                            </Application>
                            <hr />
                        </Box>
                    )}
                    <Box width="100%">
                        <Typography
                            variant="h4"
                            style={{ marginBottom: '32px' }}
                        >
                            <Trans
                                i18nKey={
                                    multipleApps
                                        ? 'Reports.adoption.usageVsDaily.multipleApps.forPeriod'
                                        : 'Reports.adoption.usageVsDaily.singleApp.forPeriod'
                                }
                                values={{
                                    appName:
                                        reportData.application[0]?.application
                                            ?.name
                                }}
                                components={{
                                    EndDate: (
                                        <>{getDate(reportSetup.endTime)}</>
                                    ),
                                    StartDate: (
                                        <>{getDate(reportSetup.startTime)}</>
                                    )
                                }}
                            />
                        </Typography>

                        <ResponsiveContainer height={300} width="100%">
                            <ComposedChart
                                data={breakdownChart}
                                width={800}
                                height={250}
                                margin={{
                                    bottom: 4,
                                    left: 4,
                                    right: 32,
                                    top: 4
                                }}
                            >
                                <XAxis
                                    dataKey="name"
                                    tickLine={false}
                                    axisLine={false}
                                    angle={-40}
                                    dy={15}
                                    height={70}
                                    dx={-35}
                                />
                                <YAxis
                                    yAxisId="left"
                                    orientation="left"
                                    tickCount={10}
                                />
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    tickFormatter={(value) =>
                                        hmsFormatter(value / 1000)
                                    }
                                />
                                <Legend />
                                <CartesianGrid
                                    stroke={theme.palette.chart.axis}
                                    strokeDasharray="3 3"
                                />
                                <Bar
                                    name="Daily Usage"
                                    yAxisId="right"
                                    dataKey="usage"
                                    barSize={40}
                                    fill={theme.palette.graph.colors[0]}
                                />
                                <Line
                                    name="Daily Users"
                                    yAxisId="left"
                                    type="linear"
                                    dataKey="users"
                                    stroke={theme.palette.graph.colors[2]}
                                    strokeWidth={3}
                                    dot={{ strokeWidth: 3 }}
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                        <hr />
                    </Box>

                    <Box display="flex" mt="32px">
                        <Box width="50%">
                            <Typography
                                variant="h5"
                                style={{ marginBottom: '8px' }}
                            >
                                {t(
                                    'Reports.adoption.allUsersInOrg.activeInactive'
                                )}
                                :
                            </Typography>
                            <PieChart
                                data={usageBreakdownArray}
                                emptyMessage={t('Common.noData')}
                                popoverContentsColumns={popoverContentsColumns}
                                responsiveContainerHeight={150}
                            />
                        </Box>
                        <Box width="50%">
                            <Typography
                                variant="h5"
                                style={{ marginBottom: '8px' }}
                            >
                                {t('Reports.adoption.orgOverview')}:
                            </Typography>
                            <ul>
                                <li className="li-with-spacing">
                                    <Trans
                                        i18nKey={
                                            multipleApps
                                                ? 'Reports.adoption.orgOverview.item1.multipleApps'
                                                : 'Reports.adoption.orgOverview.item1.singleApp'
                                        }
                                        values={{
                                            activeUsers: usageBreakdown.Active,
                                            appName:
                                                reportData?.application[0]
                                                    ?.application?.name,
                                            inactiveUsers:
                                                usageBreakdown.Inactive || 0
                                        }}
                                        components={{
                                            EndDate: (
                                                <>
                                                    {getDate(
                                                        reportSetup.endTime
                                                    )}
                                                </>
                                            ),
                                            StartDate: (
                                                <>
                                                    {getDate(
                                                        reportSetup.startTime
                                                    )}
                                                </>
                                            )
                                        }}
                                    />
                                </li>
                                <li className="li-with-spacing">
                                    {t('Reports.adoption.orgOverview.item2', {
                                        activeUsers: usageBreakdown.Active
                                    })}
                                    :
                                    <ul>
                                        {categorizedUsageBreakdownArray.map(
                                            (v) => (
                                                <li
                                                    className="li-with-spacing"
                                                    key={`categorizedUsageBreakdownArray${v.type}`}
                                                >
                                                    {t(
                                                        'Reports.adoption.orgOverview.item2.subitems',
                                                        {
                                                            type: v.type,
                                                            value: v.value
                                                        }
                                                    )}
                                                </li>
                                            )
                                        )}
                                    </ul>
                                </li>
                            </ul>
                        </Box>
                    </Box>
                    <hr />
                    <Box display="flex" mt="32px">
                        <Box width="48%">
                            <Typography
                                variant="h5"
                                style={{ marginBottom: '8px' }}
                            >
                                {t('Reports.adoption.top5Users')}
                            </Typography>
                            <ManagedTable
                                headers={headersTop5Users({ t, totalDays })}
                                renderBody={this.renderRowTop5Users}
                                renderBodyKey={null}
                                renderFooter={false}
                                rows={userChart}
                                rowsPerPage={5}
                                searchEnabled={false}
                                sortDirection="DESC"
                                sortFieldType="number"
                                sortKey="usage"
                            />
                        </Box>

                        <Box width="48%" ml="8px">
                            <Typography
                                variant="h5"
                                style={{ marginBottom: '8px' }}
                            >
                                {t('Reports.adoption.bottom5Users')}
                            </Typography>
                            <ManagedTable
                                headers={headersBottom5Users({ t, totalDays })}
                                renderBody={this.renderRowBottom5Users}
                                renderBodyKey={null}
                                renderFooter={false}
                                rows={userChart}
                                rowsPerPage={5}
                                searchEnabled={false}
                                sortDirection="ASC"
                                sortFieldType="number"
                                sortKey="usage"
                            />
                        </Box>
                    </Box>
                </Box>
                <Box
                    mt="32px"
                    width="100%"
                    data-trackid="adoptionReportUserBreakdownWrapper"
                    style={{ pageBreakBefore: 'always' }}
                >
                    <div
                        style={{
                            marginBottom: '16px',
                            marginTop: '16px',
                            width: '100%'
                        }}
                    >
                        <Typography variant="h4">
                            <Trans
                                i18nKey={
                                    multipleApps
                                        ? 'Reports.adoption.userBreakdown.multipleApps.forPeriod'
                                        : 'Reports.adoption.userBreakdown.singleApp.forPeriod'
                                }
                                values={{
                                    appName:
                                        reportData.application[0]?.application
                                            ?.name
                                }}
                                components={{
                                    EndDate: (
                                        <>{getDate(reportSetup.endTime)}</>
                                    ),
                                    StartDate: (
                                        <>{getDate(reportSetup.startTime)}</>
                                    )
                                }}
                            />
                        </Typography>
                    </div>
                    <ManagedTable
                        headers={
                            !multipleApps
                                ? headersUserBreakdownSingle({ t, totalDays })
                                : headersUserBreakdownMultiple({ t, totalDays })
                        }
                        renderBody={this.renderRowUserBreakdown}
                        renderBodyKey={null}
                        renderFooter={false}
                        rows={userChart}
                        rowsPerPage={-1}
                        searchEnabled={false}
                        sortDirection="DESC"
                        sortFieldType="number"
                        sortKey="usage"
                    />
                </Box>

                <Box width="100%" style={{ pageBreakBefore: 'always' }}>
                    <div
                        style={{
                            marginBottom: '16px',
                            marginTop: '16px',
                            width: '100%'
                        }}
                    >
                        <Typography variant="h4">
                            <Trans
                                i18nKey={
                                    multipleApps
                                        ? 'Reports.adoption.dailyBreakdown.multipleApps.forPeriod'
                                        : 'Reports.adoption.dailyBreakdown.singleApp.forPeriod'
                                }
                                values={{
                                    appName:
                                        reportData.application[0]?.application
                                            ?.name
                                }}
                                components={{
                                    EndDate: (
                                        <>{getDate(reportSetup.endTime)}</>
                                    ),
                                    StartDate: (
                                        <>{getDate(reportSetup.startTime)}</>
                                    )
                                }}
                            />
                        </Typography>
                    </div>
                    <ManagedTable
                        headers={headersDailyBreakdown}
                        renderBody={this.renderRowDailyBreakdown}
                        renderBodyKey={null}
                        renderFooter={false}
                        rows={breakdownChart}
                        rowsPerPage={-1}
                        searchEnabled={false}
                        sortDirection="ASC"
                        sortFieldType="string"
                        sortKey="name"
                    />
                </Box>
            </>
        );
    }
}

export default withTranslation()(withTheme(ProductAdoptionReports));
